import { LoadFailed } from '../components/QueryState';
import { useState, type ReactNode } from 'react';
import {
  createFileRoute,
  Link,
  redirect,
  useNavigate,
  useCanGoBack,
  useRouter,
} from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Amount, Button, Chip, Field, Input, Panel, PanelHeader, Ref, Select } from '@promanage/ui';
import { displayName } from '@promanage/contracts/person-display';
import {
  REF_PREFIX,
  formatAddress,
  formatNzDate,
  formatNzDateOr,
  formatRef,
  formatShare,
  isCurrentRole,
  parseRef,
  spanState,
  streetLine,
} from '@promanage/core';
import { RecordNotFound } from '../components/RecordNotFound';
import { TopBar } from '../components/TopBar';
import { PanelHelp } from '../components/PanelHelp';
import { RecordSkeleton } from '../components/RecordSkeleton';
import { RecordIdentity } from '../components/RecordIdentity';
import { Icon } from '../components/Icon';
import { RecordTabs } from '../components/RecordTabs';
import { RecordCommunications } from '../features/inbox/RecordCommunications';
import { PropertyListing } from '../features/property/PropertyListing';
import { PropertyTimeline } from '../features/property/PropertyTimeline';
import { readTabSearch } from '../components/tabSearch';
import { RecordFiles } from '../features/reports/RecordFiles';
import { RecordLetters } from '../features/correspondence/RecordLetters';
import { PropertyImages } from '../features/property/PropertyImages';
import { PropertyHeroImage } from '../features/property/PropertyHeroImage';
import { PropertyManagers } from '../features/property/PropertyManagers';
import { PropertyMaintenance } from '../features/maintenance/PropertyMaintenance';
import { PropertyBills } from '../features/bills/PropertyBills';
import { PropertyCompliance } from '../features/property/PropertyCompliance';
import { PropertyHealthyHomes } from '../features/property/PropertyHealthyHomes';
import { PropertyInspections } from '../features/property/PropertyInspections';
import { invalidateAccounts } from '../features/ledger/invalidate';
import { FeesPanel } from '../features/property/Fees';
import { perLabel, readRent } from '../features/property/rent';
import type { TenancySummary } from '../features/property/types';
import {
  geocodeStatusHelp,
  geocodeStatusLabel,
  geocodeStatusTone,
  healthyHomesLabel,
  managementLabel,
  propertyTypeLabel,
  spanTone,
  tenancyLabel,
} from '../features/property/shares';
import { todayIso } from '../features/property/today';
import { trpc } from '../lib/trpc';
import { LegacyCode } from '../features/legacyCodes/LegacyCode';

/**
 * One property, addressed by its display ref: /properties/PR000003.
 *
 * Same contract as /people/$ref — the URL segment is user input, so `beforeLoad` normalises
 * anything ref-shaped to the canonical form and the component answers each remaining wrong
 * shape distinctly. A ref for another entity, a typo, and a number this agency has not issued
 * are three different mistakes and only two of them are the user's to fix.
 *
 * `.index.tsx`, NOT `.tsx`, for the reason `people.$ref.index.tsx` is: under flat file routing a
 * `properties.$ref.tsx` sitting beside `properties.$ref.edit.tsx` is not its sibling, it is its
 * PARENT — and a parent with no `<Outlet />` paints itself over its child, so /edit would match
 * correctly and then render this read-only record on top of the form. Both are leaves under a
 * component-less parent instead. `routing.test.ts` pins the general rule rather than this one
 * instance.
 */
export const Route = createFileRoute('/properties/$ref/')({
  /*
   * Which section is open, in the URL. Shared with the owner, tenancy and person pages — see
   * `readTabSearch` for why it is its own import-free module.
   */
  validateSearch: readTabSearch,

  beforeLoad: ({ params, search }) => {
    const parsed = parseRef(params.ref);
    if (parsed?.entity === 'property') {
      const canonical = formatRef('property', parsed.sequence);
      // Loop-safe: formatRef of a parsed canonical ref is that same string.
      if (canonical !== params.ref) {
        throw redirect({
          to: '/properties/$ref',
          params: { ref: canonical },
          // Carried over: normalising the ref must not close the tab somebody linked to.
          search,
          replace: true,
        });
      }
    }
  },
  component: PropertyDetailPage,
});

function PropertyDetailPage() {
  const { ref } = Route.useParams();
  const parsed = parseRef(ref);

  if (!parsed) {
    return (
      <Unresolved title="Not a record reference">
        <code className="font-mono text-ink">{ref}</code> is not a record reference. Properties look
        like <code className="font-mono text-ink">PR000003</code> — two letters for the record type,
        then its number.
      </Unresolved>
    );
  }

  if (parsed.entity !== 'property') {
    return (
      <Unresolved title={`${formatRef(parsed.entity, parsed.sequence)} is not a property`}>
        That is {parsed.entity === 'owner' ? 'an' : 'a'} {parsed.entity} reference. Property
        references start with <code className="font-mono text-ink">{REF_PREFIX.property}</code>.
      </Unresolved>
    );
  }

  return <Detail refSeq={parsed.sequence} />;
}

function Detail({ refSeq }: { refSeq: number }) {
  const queryClient = useQueryClient();
  const { tab } = Route.useSearch();
  const today = todayIso();
  const [starting, setStarting] = useState(false);

  const property = useQuery(
    trpc.property.get.queryOptions(
      { refSeq },
      { meta: { errorTitle: 'Could not load this property' }, staleTime: 10_000 },
    ),
  );

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: trpc.property.get.queryKey({ refSeq }) });
    void queryClient.invalidateQueries({ queryKey: trpc.property.list.queryKey() });
    void queryClient.invalidateQueries({ queryKey: trpc.property.tenancies.queryKey() });
    // A tenancy opens a ledger account, so the ledger's own views changed too.
    invalidateAccounts(queryClient);
  };

  if (property.isPending) {
    return (
      <div className="mx-auto max-w-[var(--content-max)] pb-4">
        <TopBar title="Property" subtitle="Loading…" />
        <RecordSkeleton />
      </div>
    );
  }

  if (property.isError)
    return (
      <div className="mx-auto max-w-[var(--content-max)] pb-4">
        <TopBar title="Property" />
        <LoadFailed retry={() => void property.refetch()} retrying={property.isFetching} />
      </div>
    );
  if (!property.data) {
    return (
      <Unresolved title={`No property ${formatRef('property', refSeq)}`}>
        Nothing in this agency uses that reference. It may belong to a different agency — references
        are numbered per agency, so the same code means different records elsewhere.
      </Unresolved>
    );
  }

  const record = property.data;
  const current = record.ownershipHistory.filter((o) => o.endsOn === null);
  const historical = record.ownershipHistory.filter((o) => o.endsOn !== null);

  /*
   * What the trust account holds against this house, by owner — the mirror of the breakdown on
   * /owners/$ref. Zero positions are dropped rather than drawn at 0.00, the same call the money
   * rules make for an unused column.
   */
  const positions = record.ownerBalances.filter((p) => BigInt(p.balanceCents) !== 0n);
  const heldBy = new Map(positions.map((p) => [p.ownerId, BigInt(p.balanceCents)]));
  const currentOwnerIds = new Set(current.map((o) => o.ownerId));
  const departed = positions.filter((p) => !currentOwnerIds.has(p.ownerId));
  const anythingHeld = positions.length > 0;
  const openTenancy = record.tenancies.find((t) => t.endsOn === null) ?? null;

  // Running and upcoming lettings. `spanState` reads the dates rather than a stored flag, so a
  // tenancy that started this morning is here without anything having had to flip at midnight.
  const live = record.tenancies.filter((t) => spanState(t, today) !== 'ended');
  const finished = record.tenancies.filter((t) => spanState(t, today) === 'ended');

  return (
    <div className="mx-auto max-w-[var(--content-max)] pb-4">
      <TopBar
        title="Property"
        subtitle={`${formatRef('property', record.refSeq)} · ${propertyTypeLabel(record.type)}`}
        actions={
          <>
            <HeaderActions />
            {openTenancy ? null : (
              <Button size="sm" variant="secondary" onClick={() => setStarting(true)}>
                Start tenancy
              </Button>
            )}
            {/* Primary action, so it sits last and carries the accent — as on the person record. */}
            <Link to="/properties/$ref/edit" params={{ ref: formatRef('property', record.refSeq) }}>
              <Button size="sm">Edit</Button>
            </Link>
          </>
        }
      />

      <div className="flex flex-col gap-4">
        {/* ── Identity ─────────────────────────────────────────────────────── */}
        <RecordIdentity
          title={streetLine(record)}
          symbol={<Icon name="property" className="size-6" />}
          status={
            <Chip
              tone={spanTone(
                { startsOn: record.managementStartsOn, endsOn: record.managementEndsOn },
                today,
              )}
            >
              {managementLabel(
                { startsOn: record.managementStartsOn, endsOn: record.managementEndsOn },
                today,
              )}
            </Chip>
          }
          subtitle={formatAddress(record)}
          facts={[
            { label: 'Property type', value: propertyTypeLabel(record.type) },
            { label: 'Management starts', value: formatNzDate(record.managementStartsOn) },
            { label: 'Management ends', value: formatNzDateOr(record.managementEndsOn, 'Not set') },
            { label: 'Legacy code', value: <LegacyCode entity="property" id={record.id} /> },
          ]}
        />

        <RecordTabs
          from="/properties/$ref/"
          current={tab ?? 'overview'}
          label="Property sections"
          images
          compliance
          healthyHomes
          maintenance
          bills
          inspections
          listing
          timeline
        />

        {tab === 'communications' ? (
          <RecordCommunications record={{ type: 'property', id: record.id }} />
        ) : tab === 'documents' ? (
          /*
           * FULL WIDTH, not dropped into the overview’s side column. A document row carries a
           * file name, a size, a note and a withdrawal record, and a third-of-a-page column
           * truncates all four — which is the one thing an operator is reading it to check.
           */
          <div className="flex flex-col gap-4">
            <RecordFiles subject={{ type: 'property', id: record.id }} shelf="documents" />
            <RecordLetters subject={{ type: 'property', id: record.id }} />
          </div>
        ) : tab === 'images' ? (
          // Same reasoning as above — a photograph grid loses columns to a side rail for nothing.
          <PropertyImages propertyId={record.id} />
        ) : tab === 'maintenance' ? (
          // Full width for the reason the three above are: an outstanding job carries a summary,
          // an urgency, a state and a figure, and a third-of-a-page column truncates the summary,
          // which is the one part somebody is reading it for.
          <PropertyMaintenance
            propertyId={record.id}
            thresholdCents={record.maintenanceApprovalThresholdCents}
            onChanged={refresh}
          />
        ) : tab === 'bills' ? (
          /*
           * Full width again, and for a reason the others do not have: the spend card at the top
           * of it is a comparison between expense types, and a comparison squeezed into a third of
           * a page is a column of bars nobody can line up.
           */
          <PropertyBills propertyId={record.id} />
        ) : tab === 'inspections' ? (
          /*
           * Full width, like the four above, and for the sharpest version of the same reason: the
           * plan editor is a two-level tree of text fields, and a third of a page turns every room
           * name into an ellipsis.
           */
          <PropertyInspections
            propertyId={record.id}
            tenancyId={record.tenancies.find((tenancy) => tenancy.endsOn === null)?.id ?? null}
          />
        ) : tab === 'healthy-homes' ? (
          /*
           * Full width, and the strongest case on the page for it: the summary across the top is
           * five standards side by side, and five chips wrapped into a third-of-a-page column is
           * a vertical list nobody can read as one answer.
           */
          <PropertyHealthyHomes
            propertyId={record.id}
            propertyRefSeq={record.refSeq}
            declared={record.healthyHomes}
            declaredAssessedOn={record.healthyHomesAssessedOn}
            // The declared state lives on the property row, so a save there changes the record
            // this page is drawn from — the identity card, this fact, and the list behind it.
            onDeclarationChanged={refresh}
          />
        ) : tab === 'listing' ? (
          // Full width: the advertisement is a long form beside where it is placed, and a third of a
          // page would leave a description box nobody can write five paragraphs in.
          <PropertyListing propertyId={record.id} />
        ) : tab === 'timeline' ? (
          /*
           * Full width, and the least arguable case on the page: a timeline is a column of dated
           * rows read top to bottom, and a third of a page turns every entry into two wrapped
           * lines — which is exactly the shape that stops it being scannable, the one thing it is
           * for.
           */
          <PropertyTimeline propertyId={record.id} />
        ) : tab === 'compliance' ? (
          // Same reasoning as above — a compliance grid loses columns to a side rail for nothing.
          <PropertyCompliance
            propertyId={record.id}
            insurancePolicies={record.insurancePolicies}
            smokeAlarms={record.smokeAlarms}
            onChanged={refresh}
          />
        ) : (
          <>
            <div className="grid gap-4 lg:grid-cols-3">
              <div className="flex min-w-0 flex-col gap-4 lg:col-span-2">
                {/* ── Tenancies ────────────────────────────────────────────────── */}
                {/*
                 * An INDEX, not a workspace.
                 *
                 * Running and upcoming lettings only, one line each, every one a link to its own
                 * record. What is managed on a tenancy — the rent, who is on it, ending it — lives
                 * at /tenancies/TE000003, because it belongs to the letting rather than to the
                 * building: a house with a decade of history had a decade of rent editors stacked
                 * on this page, and only one of them was ever the tenancy anybody meant.
                 */}
                <Panel className="p-0">
                  <PanelHeader
                    title="Tenancies"
                    subtitle="Current and upcoming tenancies"
                    className="px-5 pt-5 pb-2"
                    aside={
                      <span className="text-[0.75rem] text-ink-subtle tabular-nums">
                        {live.length}
                      </span>
                    }
                  />

                  {live.length === 0 ? (
                    <p className="px-5 pb-5 text-[0.8125rem] text-ink-muted">
                      {record.tenancies.length === 0
                        ? 'Never let. Starting a tenancy opens its ledger account, where rent is credited and arrears show.'
                        : 'Nothing running here now. Every tenancy this property has had is below.'}
                    </p>
                  ) : (
                    <ul className="flex flex-col">
                      {live.map((tenancy) => (
                        <TenancyRow key={tenancy.id} tenancy={tenancy} today={today} />
                      ))}
                    </ul>
                  )}
                </Panel>

                {/*
                 * ── Owners ─────────────────────────────────────────────────────
                 *
                 * Directly under Tenancies — who is paying rent and who that rent is ultimately
                 * for read as one story, not two panels a scroll apart.
                 *
                 * THE SHARE AND THE MONEY, in one list. The share says how this house's rent WILL be
                 * divided; the balance says what is sitting in the trust account against it now, for
                 * each of them. They are different questions and they need not agree — two owners of
                 * half a house each hold different amounts of it the week one of them is paid out and
                 * the other is not due — which is a reason to read them together, not a reason to show
                 * only one.
                 */}
                <Panel className="p-0">
                  <PanelHeader
                    title="Owners"
                    subtitle={
                      anythingHeld
                        ? 'Their shares, and what the trust account holds against this property'
                        : 'Their shares of this property'
                    }
                    className="px-5 pt-5 pb-2"
                  />
                  <ul className="flex flex-col">
                    {current.map((interest) => (
                      <li
                        key={interest.id}
                        className="flex items-baseline justify-between gap-3 border-t border-rule px-5 py-2.5"
                      >
                        <span className="min-w-0">
                          <span className="block truncate text-[0.8125rem] font-medium">
                            {interest.name}
                          </span>
                          <Ref entity="owner" sequence={interest.ownerRefSeq} />
                        </span>
                        <span className="flex shrink-0 flex-col items-end">
                          <span className="text-[0.8125rem] font-semibold tabular-nums">
                            {formatShare(interest.shareBp)}
                          </span>
                          <HeldForOwner cents={heldBy.get(interest.ownerId) ?? null} />
                        </span>
                      </li>
                    ))}

                    {/*
                     * An owner who has SOLD and still holds money against the house. Rare, real, and
                     * exactly the row a list built by hanging the balances off the current ownerships
                     * would drop without saying so — leaving a page whose figures do not add up to the
                     * money the trust account is actually holding against this property.
                     */}
                    {departed.map((position) => (
                      <li
                        key={position.ownerId}
                        className="flex items-baseline justify-between gap-3 border-t border-rule px-5 py-2.5"
                      >
                        <span className="min-w-0">
                          <span className="block truncate text-[0.8125rem] font-medium">
                            {position.ownerName}
                          </span>
                          <span className="flex flex-wrap items-center gap-2">
                            <Ref entity="owner" sequence={position.ownerRefSeq} />
                            <Chip tone="neutral">No longer an owner</Chip>
                          </span>
                        </span>
                        <span className="flex shrink-0 flex-col items-end">
                          <HeldForOwner cents={BigInt(position.balanceCents)} />
                        </span>
                      </li>
                    ))}
                  </ul>
                  <PanelHelp>
                    Shares are basis points and always total 100%, on every date they cover.
                    Changing them needs a trust role — it redirects money that is already flowing.
                    {anythingHeld
                      ? ' The amounts are this property’s part of each owner’s trust balance, which is paid out as a whole.'
                      : null}
                  </PanelHelp>
                </Panel>

                {/*
                 * Kept reachable, deliberately. A finished tenancy is not clutter — it is what a
                 * bond dispute, an arrears claim or a damage claim is about, and those arrive
                 * months after the keys came back. Collapsed rather than dropped, so the current
                 * letting stays the thing this page is about.
                 */}
                {finished.length > 0 ? (
                  <PreviousTenancies tenancies={finished} today={today} />
                ) : null}

                {/* ── Ownership history ────────────────────────────────────────── */}
                {historical.length > 0 ? (
                  <Panel className="p-0">
                    <PanelHeader
                      title="Previous ownership"
                      subtitle="Previous owners and their ownership dates"
                      className="px-5 pt-5 pb-2"
                    />
                    <ul className="flex flex-col">
                      {historical.map((interest) => (
                        <li
                          key={interest.id}
                          className="flex flex-wrap items-baseline gap-x-3 gap-y-1 border-t border-rule px-5 py-2.5 text-[0.8125rem]"
                        >
                          <span className="min-w-0 flex-1 truncate text-ink-muted">
                            {interest.name}
                          </span>
                          <span className="tabular-nums">{formatShare(interest.shareBp)}</span>
                          <span className="text-[0.75rem] text-ink-subtle tabular-nums">
                            {interest.startsOn} – {interest.endsOn}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </Panel>
                ) : null}
              </div>

              <div className="flex flex-col gap-4">
                {/*
                 * ── The hero image ───────────────────────────────────────────────
                 *
                 * First in the narrow column rather than a full-width banner above everything —
                 * docs/DESIGN_SYSTEM.md §2b rules out "a giant centred marketing hero", and this
                 * is meant to sit beside Details the way a compact photo sits beside a profile,
                 * not push the arrears figure below the fold.
                 */}
                <PropertyHeroImage
                  propertyId={record.id}
                  propertyRef={formatRef('property', record.refSeq)}
                  heroImageDocumentId={record.heroImageDocumentId}
                  onChanged={refresh}
                />

                <PropertyManagers propertyId={record.id} managers={record.managers} />

                {/* ── The property itself ──────────────────────────────────────── */}
                <Panel className="p-0">
                  <PanelHeader title="Details" className="px-5 pt-5 pb-2" />
                  <dl className="flex flex-col gap-2.5 border-t border-rule px-5 py-4 text-[0.8125rem]">
                    <Detail_ label="Type" value={propertyTypeLabel(record.type)} />
                    <Detail_
                      label="Rooms"
                      value={
                        record.bedrooms === null && record.bathrooms === null
                          ? '—'
                          : `${record.bedrooms ?? '?'} bed · ${record.bathrooms ?? '?'} bath`
                      }
                    />
                    {/*
                     * SHOWN HERE, SET ON THE TAB. It is a fact worth having on the record at a
                     * glance — it is what a compliance statement is written from — but there is
                     * one editor for it now, beside the five standards it is a claim about, so
                     * this is a link rather than a field.
                     */}
                    <Detail_
                      label="Healthy Homes"
                      value={
                        <Link
                          from="/properties/$ref/"
                          search={{ tab: 'healthy-homes' }}
                          replace
                          className="text-accent hover:underline"
                        >
                          {healthyHomesLabel(record.healthyHomes)}
                          {record.healthyHomesAssessedOn ? (
                            <span className="ml-1.5 text-[0.75rem] text-ink-subtle tabular-nums">
                              assessed {formatNzDate(record.healthyHomesAssessedOn)}
                            </span>
                          ) : null}
                        </Link>
                      }
                    />
                  </dl>

                  {/*
                   * WHY THERE IS NO PIN, on the record rather than only on the map.
                   *
                   * The map can say a property is missing from it; only this page can say why,
                   * and this is the page somebody is on when they wonder. A located property
                   * gets one quiet line — the whole panel would be noise for the 95% of the
                   * portfolio that resolved first time — and an unlocated one gets the sentence
                   * that says what to do about it.
                   */}
                  <MapLocation record={record} />

                  {record.notes ? (
                    <p className="border-t border-rule px-5 py-4 text-[0.8125rem] leading-relaxed whitespace-pre-wrap text-ink-muted">
                      {record.notes}
                    </p>
                  ) : null}
                </Panel>

                {/* Everything this agency charges on this house — the rate on rent collected, and
                    what each inspection costs. */}
                <FeesPanel
                  propertyId={record.id}
                  feeBp={record.managementFeeBp}
                  inspectionFeeCents={record.inspectionFeeCents}
                  onChanged={refresh}
                />
              </div>
            </div>
          </>
        )}
      </div>

      {starting ? (
        <StartTenancyDialog
          propertyId={record.id}
          address={streetLine(record)}
          today={today}
          onClose={() => setStarting(false)}
          onDone={() => {
            setStarting(false);
            refresh();
          }}
        />
      ) : null}
    </div>
  );
}

/**
 * Back, then the list.
 *
 * The two are not the same thing and both earn their place: "Back" returns to wherever you
 * came from — the list with your filters, sort and scroll intact, or another person's record
 * if you arrived through a relationship — while "All properties" is a fixed destination that
 * resets those.
 *
 * Back is hidden, not disabled, when there is no in-app history: the ref is addressable, so
 * arriving by pasted URL in a fresh tab is a real entry path, and there a browser back would
 * throw the user out of the app entirely.
 */
function HeaderActions() {
  const router = useRouter();
  const canGoBack = useCanGoBack();

  return (
    <>
      {canGoBack ? (
        <Button variant="secondary" size="sm" onClick={() => router.history.back()}>
          <Icon name="arrow-right" size={14} className="rotate-180" />
          Back
        </Button>
      ) : null}
      <Link to="/properties">
        <Button variant="secondary" size="sm">
          All properties
        </Button>
      </Link>
    </>
  );
}

/**
 * One letting, as a line.
 *
 * The whole row is the link, not just the ref: this is a list whose only purpose is to get you
 * to the tenancy, and a 12px target inside a 60px row is a worse version of the same journey.
 *
 * Rent is shown but not editable here. A figure is what tells you which tenancy you are looking
 * at when a property has two in a year; changing it belongs on the record that owns it.
 */
/**
 * What this property holds for one owner, under their share.
 *
 * NULL IS NOT ZERO. An owner with no position has had nothing posted against this house at all —
 * a management starting next month, a property whose first rent has not arrived — and drawing
 * "0.00" against them states a balance that was never measured. Blank reads as "not this way",
 * which is what the money rules ask for and why a bank statement scans quickly.
 *
 * A deficit is drawn plainly, with a word beside it rather than a colour alone. One house of one
 * owner may sit negative while the owner may not: the rest of their portfolio covered it, which
 * invariant 3 permits and a repair in a quiet month causes routinely.
 */
function HeldForOwner({ cents }: { cents: bigint | null }) {
  if (cents === null) return null;

  return (
    <>
      <Amount
        value={cents}
        currency
        notation="accounting"
        className="text-[0.75rem] text-ink-muted"
      />
      <span className="text-[0.6875rem] text-ink-subtle">
        {cents < 0n ? 'covered by their other houses' : 'held'}
      </span>
    </>
  );
}

function TenancyRow({ tenancy, today }: { tenancy: TenancySummary; today: string }) {
  const rent = readRent(tenancy.rent, today).current;
  // Covering today, not merely open-ended — a tenancy under notice ends its roles on the day
  // the tenants leave, and until then they are the people living there.
  const people = tenancy.people.filter((person) => isCurrentRole(person, today));

  return (
    <li className="border-t border-rule">
      <Link
        to="/tenancies/$ref"
        params={{ ref: formatRef('tenancy', tenancy.refSeq) }}
        className="flex flex-wrap items-center gap-x-3 gap-y-1.5 px-5 py-3 transition-colors duration-[var(--duration-fast)] hover:bg-surface-sunk"
      >
        <Ref entity="tenancy" sequence={tenancy.refSeq} />
        <Chip tone={spanTone(tenancy, today)}>{tenancyLabel(tenancy, today)}</Chip>

        <span className="min-w-0 flex-1">
          <span className="block truncate text-[0.8125rem] font-medium">
            {people.length === 0 ? (
              <span className="font-normal text-ink-subtle">Nobody attached</span>
            ) : (
              people.map(displayName).join(', ')
            )}
          </span>
          <span className="block text-[0.75rem] text-ink-subtle tabular-nums">
            {formatNzDate(tenancy.startsOn)} – {formatNzDateOr(tenancy.endsOn, 'present')}
            <span className="ml-1.5">
              {tenancy.termType === 'fixed'
                ? // The AGREED end of the term, which is a different fact from the day the
                  // tenancy actually ends. A tenant who vacates early ends the tenancy; the
                  // fixed term is what was signed.
                  `fixed to ${formatNzDateOr(tenancy.fixedTermEndsOn, '—')}`
                : 'periodic'}
            </span>
          </span>
        </span>

        <span className="flex shrink-0 flex-col items-end">
          {rent ? (
            <>
              <Amount value={rent.amountCents} currency className="text-[0.875rem] font-semibold" />
              <span className="text-[0.6875rem] text-ink-subtle">{perLabel(rent.frequency)}</span>
            </>
          ) : (
            // Not an error state. A tenancy signed today has no rent on it until somebody
            // records what the agreement says, and that is a normal few minutes.
            <span className="text-[0.75rem] text-ink-subtle">Rent not set</span>
          )}
        </span>

        <Icon name="arrow-right" size={14} className="shrink-0 text-ink-subtle" />
      </Link>
    </li>
  );
}

/**
 * The tenancies that have finished here, collapsed.
 *
 * Behind a disclosure rather than dropped: "who was in it in March" is asked about bond, damage
 * and arrears long after the keys came back, and the tenancy record is where the answer is. A
 * property let for a decade would otherwise bury this year's letting under ten of them.
 */
function PreviousTenancies({ tenancies, today }: { tenancies: TenancySummary[]; today: string }) {
  const [open, setOpen] = useState(false);

  return (
    <Panel className="p-0">
      <PanelHeader
        title="Previous tenancies"
        subtitle={
          open ? 'Kept for bond, arrears and damage claims' : `${tenancies.length} finished here`
        }
        className="px-5 pt-5 pb-2"
        aside={
          <Button size="sm" variant="ghost" onClick={() => setOpen(!open)} aria-expanded={open}>
            {open ? 'Hide' : 'Show'}
          </Button>
        }
      />
      {open ? (
        <ul className="flex flex-col">
          {tenancies.map((tenancy) => (
            <TenancyRow key={tenancy.id} tenancy={tenancy} today={today} />
          ))}
        </ul>
      ) : (
        <div className="pb-3" />
      )}
    </Panel>
  );
}

/** A label/value pair in the details list. Underscored to avoid shadowing the page component. */
/**
 * What the address lookup last said, and what the operator should do about it.
 *
 * ── THE GAP THIS FILLS ───────────────────────────────────────────────────────────────────────
 *
 * A corrected address whose pin never came back was unexplainable from anywhere in the product:
 * no error on the form (the save succeeded — the coordinate is resolved afterwards, by the
 * `geocode` queue), nothing in the audit log (no person did it), and a `latitude` of null that
 * read the same whether the street was misspelled or LINZ was down. The row now records which,
 * and this is where a property manager reads it.
 *
 * `geocodeAttempts` is shown only once it is worth showing. "Tried once, an hour ago" is the
 * normal case and says nothing; "tried 40 times since June" is somebody's cue to escalate.
 */
function MapLocation({
  record,
}: {
  record: {
    geocodeStatus: string;
    geocodeDetail: string | null;
    geocodeAttempts: number;
    geocodedAt: string | null;
  };
}) {
  const located = record.geocodeStatus === 'located';
  const help = geocodeStatusHelp(record.geocodeStatus);

  return (
    <div className="flex flex-col gap-1.5 border-t border-rule px-5 py-4">
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-[0.6875rem] font-semibold tracking-[0.06em] text-ink-subtle uppercase">
          On the map
        </span>
        {located ? (
          <span className="text-[0.8125rem] text-ink-muted">Located</span>
        ) : (
          <Chip tone={geocodeStatusTone(record.geocodeStatus)}>
            {geocodeStatusLabel(record.geocodeStatus)}
          </Chip>
        )}
      </div>

      {located ? null : (
        <>
          {help ? <p className="text-[0.8125rem] leading-relaxed text-ink-muted">{help}</p> : null}
          <p className="text-[0.75rem] text-ink-subtle">
            {record.geocodedAt === null
              ? 'Not looked up yet.'
              : `Last checked ${formatNzDate(record.geocodedAt.slice(0, 10))}`}
            {record.geocodeAttempts > 1 ? ` · ${record.geocodeAttempts} attempts` : ''}
            {/* The technical line, for whoever is actually debugging it rather than fixing an
                address. Deliberately last and deliberately quiet. */}
            {record.geocodeDetail === null ? '' : ` · ${record.geocodeDetail}`}
          </p>
        </>
      )}
    </div>
  );
}

function Detail_({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-[0.6875rem] font-semibold tracking-[0.06em] text-ink-subtle uppercase">
        {label}
      </dt>
      <dd className="min-w-0 text-right">{value}</dd>
    </div>
  );
}

function StartTenancyDialog({
  propertyId,
  address,
  today,
  onClose,
  onDone,
}: {
  propertyId: string;
  address: string;
  today: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const navigate = useNavigate();
  const [termType, setTermType] = useState<'fixed' | 'periodic'>('periodic');
  const [startsOn, setStartsOn] = useState(today);
  const [fixedTermEndsOn, setFixedTermEndsOn] = useState('');

  const create = useMutation(
    trpc.property.createTenancy.mutationOptions({
      meta: { errorTitle: 'Could not start that tenancy' },
      onSuccess: (created) => {
        onDone();
        // Straight to the new record. A tenancy with no rent and no tenant on it is not
        // finished, and both of those are set there — dropping the operator back on the
        // property page makes them find what they just created before they can carry on.
        void navigate({
          to: '/tenancies/$ref',
          params: { ref: formatRef('tenancy', created.refSeq) },
        });
      },
    }),
  );

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    create.mutate({
      propertyId,
      termType,
      startsOn,
      ...(termType === 'fixed' ? { fixedTermEndsOn } : {}),
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <Panel className="w-full max-w-md p-5">
        <PanelHeader title="Start a tenancy" subtitle={address} className="p-0 pb-3" />

        <form onSubmit={submit} noValidate className="flex flex-col gap-3">
          <Field label="Term" required>
            {(props) => (
              <Select
                {...props}
                value={termType}
                onChange={(e) => setTermType(e.target.value === 'fixed' ? 'fixed' : 'periodic')}
              >
                <option value="periodic">Periodic — no agreed end date</option>
                <option value="fixed">Fixed term</option>
              </Select>
            )}
          </Field>

          <Field label="Starts on" required>
            {(props) => (
              <Input
                {...props}
                type="date"
                value={startsOn}
                onChange={(e) => setStartsOn(e.target.value)}
              />
            )}
          </Field>

          {termType === 'fixed' ? (
            <Field
              label="Fixed term ends"
              required
              hint="What was signed — not the day the tenancy actually ends"
            >
              {(props) => (
                <Input
                  {...props}
                  type="date"
                  value={fixedTermEndsOn}
                  onChange={(e) => setFixedTermEndsOn(e.target.value)}
                />
              )}
            </Field>
          ) : null}

          <p className="text-[0.75rem] text-ink-subtle">
            The rent and who lives here are set on the tenancy’s own record, which opens next.
            People are attached as a role, so one human stays one record however many tenancies they
            have been part of.
          </p>

          <div className="flex gap-2">
            <Button
              type="submit"
              disabled={create.isPending || (termType === 'fixed' && fixedTermEndsOn === '')}
            >
              {create.isPending ? 'Starting' : 'Start tenancy'}
            </Button>
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
          </div>
        </form>
      </Panel>
    </div>
  );
}

/**
 * The URL did not resolve to a property.
 *
 * Three messages rather than one 404, for the same reason /people/$ref has three: a ref for
 * another entity, a typo, and a number this agency has not issued are different mistakes.
 */
function Unresolved({ title, children }: { title: string; children: ReactNode }) {
  return (
    <RecordNotFound
      record="Property"
      title={title}
      back={{ label: 'Back to properties', link: { to: '/properties' } }}
    >
      {children}
    </RecordNotFound>
  );
}
